var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { createRef, useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { useInView } from 'react-intersection-observer';
import scrollIntoView from 'scroll-into-view-if-needed';
import { useCtx } from '../Canvas';
import { mergeRefs } from '../mergeRefs';
import { DropdownContext } from './DropdownContext';
import { Group } from './Group';
import { MenuContext } from './MenuContext';
import { Portal } from './Portal';
import s from './styles.module.css.json';
function ctxHasSizingUtils(ctx) {
    return 'isAutoResizerActive' in ctx;
}
var MenuDesktopContainer = React.forwardRef(function (_a, ref) {
    var children = _a.children;
    return (React.createElement(Portal, null,
        React.createElement("div", { className: s['Dropdown__menu-container'], style: { zIndex: 1000 }, ref: ref }, children)));
});
function getAbsoluteHeight(el) {
    var styles = window.getComputedStyle(el);
    var margin = Number.parseFloat(styles.marginTop) +
        Number.parseFloat(styles.marginBottom);
    return Math.ceil(el.offsetHeight + margin);
}
function getAbsoluteWidth(el) {
    var styles = window.getComputedStyle(el);
    var margin = Number.parseFloat(styles.marginLeft) +
        Number.parseFloat(styles.marginRight);
    return Math.ceil(el.offsetWidth + margin);
}
function setPosition(panel, parent, alignment, isAutoResizerActive) {
    var rect = parent.getBoundingClientRect();
    var height = getAbsoluteHeight(panel);
    var parentWidth = getAbsoluteWidth(parent);
    var width = getAbsoluteWidth(panel);
    if (alignment === 'left') {
        // eslint-disable-next-line no-param-reassign
        panel.style.left = "".concat(rect.left + window.pageXOffset, "px");
    }
    else {
        // eslint-disable-next-line no-param-reassign
        panel.style.left = "".concat(rect.left + window.pageXOffset + parentWidth - width, "px");
    }
    var windowHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    var menu = panel.querySelector(".".concat(s.Dropdown__menu));
    if (!menu) {
        return;
    }
    var styles = window.getComputedStyle(menu);
    var marginTop = Number.parseFloat(styles.marginTop);
    var fitsBelow = rect.bottom + height <= windowHeight;
    var fitsAbove = rect.top - height > 0;
    if (!fitsBelow && fitsAbove) {
        // eslint-disable-next-line no-param-reassign
        panel.style.top = "".concat(rect.bottom - height, "px");
    }
    else if (fitsBelow) {
        // eslint-disable-next-line no-param-reassign
        panel.style.top = "".concat(rect.bottom, "px");
    }
    else {
        var spaceAbove = rect.top;
        var spaceBelow = windowHeight - rect.bottom;
        if (spaceBelow > spaceAbove) {
            // eslint-disable-next-line no-param-reassign
            panel.style.top = "".concat(rect.bottom, "px");
            if (!isAutoResizerActive) {
                menu.style.maxHeight = "".concat(windowHeight - rect.bottom - marginTop - 10, "px");
            }
        }
        else {
            // eslint-disable-next-line no-param-reassign
            panel.style.top = '0px';
            if (!isAutoResizerActive) {
                menu.style.maxHeight = "".concat(rect.top - marginTop, "px");
            }
        }
    }
    // eslint-disable-next-line no-param-reassign
    panel.style.visibility = 'visible';
}
export var Menu = function (_a) {
    var children = _a.children, _b = _a.alignment, alignment = _b === void 0 ? 'left' : _b;
    var ctx = useCtx();
    var closeMenu = useContext(DropdownContext).closeMenu;
    var childrenArray = React.Children.toArray(children);
    var _c = useState(''), searchTerm = _c[0], setSearchTerm = _c[1];
    var _d = useState([]), options = _d[0], setOptions = _d[1];
    var handleChange = useCallback(function (e) {
        setSearchTerm(e.target.value);
    }, [setSearchTerm]);
    var anyGroup = childrenArray.some(function (child) {
        return typeof child === 'object' && 'type' in child && child.type === Group;
    });
    var addOption = useCallback(function (id) {
        setOptions(function (old) { return __spreadArray(__spreadArray([], old, true), [{ id: id }], false); });
        return function () {
            setOptions(function (old) { return old.filter(function (x) { return x.id !== id; }); });
        };
    }, [setOptions]);
    var setClickHandlerForOption = useCallback(function (id, handler) {
        setOptions(function (old) {
            return old.map(function (x) { return (x.id !== id ? x : __assign(__assign({}, x), { handler: handler })); });
        });
    }, [setOptions]);
    var contentRef = useRef(null);
    var handleKeyDown = useCallback(function (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            closeMenu();
        }
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
            if (!contentRef.current) {
                return;
            }
            var delta = event.key === 'ArrowUp' ? -1 : 1;
            var selectedOption_1 = contentRef.current.querySelector(".".concat(s['Dropdown__menu__option--is-selected']));
            var nextOption = void 0;
            if (!selectedOption_1) {
                nextOption = contentRef.current.querySelector(".".concat(s.Dropdown__menu__option));
            }
            else {
                var elements = Array.from(contentRef.current.querySelectorAll(".".concat(s.Dropdown__menu__option)));
                var index = elements.findIndex(function (el) { return el === selectedOption_1; });
                var nextIndex = index + delta < 0
                    ? elements.length - 1
                    : (index + delta) % elements.length;
                nextOption = elements[nextIndex];
                selectedOption_1.classList.remove(s['Dropdown__menu__option--is-selected']);
            }
            if (nextOption) {
                nextOption.classList.add(s['Dropdown__menu__option--is-selected']);
                scrollIntoView(nextOption, {
                    scrollMode: 'if-needed',
                    behavior: 'auto',
                });
            }
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            if (!contentRef.current) {
                return;
            }
            var selectedOption = contentRef.current.querySelector(".".concat(s['Dropdown__menu__option--is-selected']));
            if (selectedOption) {
                var id_1 = selectedOption.dataset.optionId;
                var option = options.find(function (x) { return x.id === id_1; });
                if (option === null || option === void 0 ? void 0 : option.handler) {
                    option.handler(event);
                }
            }
        }
    }, [options]);
    var _e = useInView({
        threshold: 0,
        rootMargin: '0px 0px 0px 0px',
        triggerOnce: false,
    }), observerRef = _e[0], inView = _e[1], entry = _e[2];
    var parentRef = createRef();
    var menuRef = createRef();
    var reposition = useCallback(function () {
        if (menuRef.current && parentRef.current) {
            var isAutoResizerActive = ctxHasSizingUtils(ctx) && ctx.isAutoResizerActive();
            setPosition(menuRef.current, parentRef.current, alignment, isAutoResizerActive);
        }
    }, [menuRef, parentRef, alignment]);
    useEffect(function () {
        reposition();
    }, [inView, entry === null || entry === void 0 ? void 0 : entry.intersectionRatio]);
    useEffect(function () {
        if (menuRef.current && parentRef.current) {
            var resizeObserver_1 = new ResizeObserver(reposition);
            resizeObserver_1.observe(menuRef.current);
            resizeObserver_1.observe(parentRef.current);
            return function () {
                resizeObserver_1.disconnect();
            };
        }
        return undefined;
    }, [menuRef, reposition]);
    useEffect(function () {
        window.addEventListener('resize', reposition);
        return function () { return window.removeEventListener('resize', reposition); };
    }, [reposition]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: mergeRefs(observerRef, parentRef), className: s.Dropdown__spacer }),
        React.createElement(MenuDesktopContainer, { ref: menuRef },
            React.createElement("div", { className: s.Dropdown__menu },
                options.length > 5 && (React.createElement("div", { className: s.Dropdown__menu__search },
                    React.createElement("input", { type: "text", value: searchTerm || '', onChange: handleChange, onKeyDown: handleKeyDown, placeholder: "Search...", className: s.Dropdown__menu__search__input }))),
                React.createElement(MenuContext.Provider, { value: { searchTerm: searchTerm, addOption: addOption, setClickHandlerForOption: setClickHandlerForOption } },
                    React.createElement("div", { className: s.Dropdown__menu__inner, ref: contentRef }, anyGroup ? (children) : (React.createElement("div", { className: s.Dropdown__menu__group__content }, children))))))));
};
//# sourceMappingURL=Menu.js.map